import { useContext, useLayoutEffect, useRef } from 'react'
import Popper from 'popper.js'
import ReactDOM from 'react-dom'

import { CssNamespaceContext, useOnUnmount } from '@wf-sl/common-utilities'

import { MODIFIERS_DEFAULT, MODIFIERS_NO_ARROW, createDefaultVirtualPopperReferenceElement } from './popover-utils'

export function usePopper(
    component,
    { additionalModifiers, appendToParent, arrowClassName, contentClassName, isActive, options, virtualElement }
) {
    const namespaceClass = useContext(CssNamespaceContext)
    const isInitializing = useRef(false)
    const targetComponentRef = useRef()
    const contentRef = useRef(document.createElement('div'))
    const popper = useRef(null)

    // prevent updates from additional modifiers. they are not changed once the popper is initialized anyway
    const additionalModifierersRef = useRef(additionalModifiers)
    additionalModifierersRef.current = additionalModifiers

    useLayoutEffect(() => {
        if (isInitializing.current) {
            return
        }

        if (isActive && !popper.current) {
            isInitializing.current = true

            const target = targetComponentRef.current || virtualElement || createDefaultVirtualPopperReferenceElement()
            ReactDOM.render(component, contentRef.current, () => {
                const contentConfig = {
                    arrowClassNames:
                        targetComponentRef.current && typeof arrowClassName === 'string'
                            ? arrowClassName.split(' ')
                            : [],
                    classNames: typeof contentClassName === 'string' ? contentClassName.split(' ') : [],
                    content: contentRef.current,
                    contentType: 'node'
                }
                if (appendToParent && targetComponentRef.current.parentElement) {
                    contentConfig.parent = targetComponentRef.current.parentElement
                } else if (namespaceClass) {
                    const parentElement = document.querySelector('#popover-parent') || document.createElement('div')
                    parentElement.setAttribute('id', 'popover-parent')
                    parentElement.classList.add(namespaceClass)
                    contentConfig.parent = document?.body.appendChild(parentElement)
                }

                const modifiers = [
                    ...(options?.modifiers || (targetComponentRef.current ? MODIFIERS_DEFAULT : MODIFIERS_NO_ARROW)),
                    ...(additionalModifierersRef.current || []),
                    'applyStyle'
                ]

                const popperOptions = {
                    boundariesPadding: 50,
                    placement: 'top',
                    positionFixed: true,
                    removeOnDestroy: true,
                    ...(options || {}),
                    modifiers
                }

                popper.current = new Popper(target, contentConfig, popperOptions)
                isInitializing.current = false
            })
        } else if (isActive) {
            ReactDOM.render(component, contentRef.current, () => {
                typeof popper.current?.update === 'function' && popper.current.update()
            })
        } else if (typeof popper.current?.destroy === 'function') {
            ReactDOM.unmountComponentAtNode(contentRef.current)
            popper.current.destroy()
            popper.current = null
        }
    }, [
        isActive,
        component,
        targetComponentRef,
        options,
        virtualElement,
        arrowClassName,
        contentClassName,
        appendToParent,
        namespaceClass
    ])

    useLayoutEffect(() => {
        if (popper.current) {
            popper.current?._popper?.setAttribute('class', contentClassName)
        }
    }, [contentClassName, namespaceClass])

    useLayoutEffect(() => {
        const popperElem = popper.current?._popper
        const arrowSelector = popper.current?._options?.arrowElement
        if (popperElem && typeof arrowSelector === 'string') {
            popperElem.querySelector(arrowSelector)?.setAttribute('class', arrowClassName)
        } else if (popperElem && typeof arrowSelector?.setAttribute === 'function') {
            arrowSelector.setAttribute('class', arrowClassName)
        }
    }, [arrowClassName])

    useOnUnmount(() => {
        if (typeof popper.current?.destroy === 'function') {
            ReactDOM.unmountComponentAtNode(contentRef.current)
            popper.current.destroy()
        }
    })

    return targetComponentRef
}
